"use client"

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react"

import { toast } from "../shadcnBase/ShadcnSonner"
import {
  createProductUndoCoordinator,
  getProductUndoHotkeyLabel,
  PRODUCT_UNDO_DURATION_MS,
  shouldHandleProductUndoHotkey,
} from "../lib/productUndo"
import type { ProductUndoAction, ProductUndoCoordinator } from "../lib/productUndo"

const PRODUCT_UNDO_TOAST_ID = "aix-product-undo"

interface ProductUndoContextValue {
  clearUndo: (actionId?: string) => void
  offerUndo: (action: ProductUndoAction) => void
}

const ProductUndoContext = createContext<ProductUndoContextValue | null>(null)

export const ProductUndoProvider = ({ children }: { children: React.ReactNode }) => {
  const coordinatorRef = useRef<ProductUndoCoordinator | null>(null)
  if (!coordinatorRef.current) {
    coordinatorRef.current = createProductUndoCoordinator({
      onExpire: () => toast.dismiss(PRODUCT_UNDO_TOAST_ID),
    })
  }

  const runUndo = useCallback(() => {
    const action = coordinatorRef.current?.take()
    if (!action) return
    toast.dismiss(PRODUCT_UNDO_TOAST_ID)
    void Promise.resolve()
      .then(() => action.undo())
      .then(
        (didUndo) => {
          if (didUndo !== false) toast.success(action.successMessage ?? "Undone")
        },
        () => toast.error(action.errorMessage ?? "Couldn't undo that change"),
      )
  }, [])

  const offerUndo = useCallback(
    (action: ProductUndoAction) => {
      coordinatorRef.current?.offer(action)
      toast(action.message, {
        id: PRODUCT_UNDO_TOAST_ID,
        duration: PRODUCT_UNDO_DURATION_MS,
        action: {
          label: `Undo ${getProductUndoHotkeyLabel()}`,
          onClick: () => void runUndo(),
        },
        onAutoClose: () => coordinatorRef.current?.clear(action.id),
        onDismiss: () => coordinatorRef.current?.clear(action.id),
      })
    },
    [runUndo],
  )

  const clearUndo = useCallback((actionId?: string) => {
    if (!coordinatorRef.current?.clear(actionId)) return
    toast.dismiss(PRODUCT_UNDO_TOAST_ID)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!coordinatorRef.current?.peek() || !shouldHandleProductUndoHotkey(event)) return
      event.preventDefault()
      void runUndo()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [runUndo])

  useEffect(
    () => () => {
      coordinatorRef.current?.clear()
      toast.dismiss(PRODUCT_UNDO_TOAST_ID)
    },
    [],
  )

  const value = useMemo(() => ({ clearUndo, offerUndo }), [clearUndo, offerUndo])
  return <ProductUndoContext.Provider value={value}>{children}</ProductUndoContext.Provider>
}

export const useProductUndo = (): ProductUndoContextValue => {
  const value = useContext(ProductUndoContext)
  if (!value) throw new Error("useProductUndo must be used within ProductUndoProvider")
  return value
}
